import { isBefore, set, startOfDay } from "date-fns";

import { formatDateTime } from "@performyard/py-components";
import type {
	PostMeetingAnalysisDecision,
	PostMeetingAnalysisResponseStreamJson,
	PostMeetingAnalysisSettings,
} from "@performyard/service-contracts";

import { POST_MEETING_ANALYSIS_CONFIG, config } from "../config";
import { isNoDataPlaceholder } from "./formatPrepAsPlainText";

const { sectionTitleMap, noDecisionsText, noMissedCommitmentsText, generalDecisionsLabel } =
	POST_MEETING_ANALYSIS_CONFIG;

/** Display names for the two roles the LLM can attribute work to. */
export type AnalysisNamesByRole = { manager: string; direct_report: string };

/** First token of a full name, e.g. "Jenny Smith" → "Jenny". */
export const toFirstName = (name: string): string => name.trim().split(/\s+/)[0] || name;

/** Parses the LLM's free-text due date and normalizes its time to 5pm local (matching the action-item due-date policy); null when missing or unparseable. */
export const parseCommitmentDueDate = (dueDate: string | null): Date | null => {
	if (!dueDate) return null;
	// A bare ISO date ("YYYY-MM-DD") is parsed as UTC midnight, which renders as the previous day
	// in negative-UTC timezones. Construct it in local time so the day is preserved regardless of TZ.
	const dateOnly = dueDate.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
	const parsed = dateOnly
		? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
		: new Date(dueDate);
	if (Number.isNaN(parsed.getTime())) return null;
	return set(parsed, config.assignment.defaultDueTime);
};

/**
 * The ISO timestamp to store when adding a commitment as an action item; null for missing, unparseable,
 * or past due dates. Mirrors createActionItem's day-granular past-date rejection so what we display and
 * what we store stay in lockstep with the backend.
 */
export const toActionItemDueDate = (dueDate: string | null): string | null => {
	const parsed = parseCommitmentDueDate(dueDate);
	if (!parsed || isBefore(startOfDay(parsed), startOfDay(new Date()))) return null;
	return parsed.toISOString();
};

type DecisionOwnerRole = PostMeetingAnalysisDecision["ownerRole"];

/**
 * Groups decisions by owner role. Owner-less decisions (`ownerRole` null) form the
 * "General Decisions" group, which always renders first; the manager and direct-report
 * groups follow it, keeping their first-appearance order.
 */
export const groupDecisionsByOwner = (
	decisions: PostMeetingAnalysisDecision[]
): { ownerRole: DecisionOwnerRole; decisions: string[] }[] => {
	const groups: { ownerRole: DecisionOwnerRole; decisions: string[] }[] = [];
	for (const { decision, ownerRole } of decisions) {
		const existing = groups.find((g) => g.ownerRole === ownerRole);
		if (existing) {
			existing.decisions.push(decision);
		} else {
			groups.push({ ownerRole, decisions: [decision] });
		}
	}
	// Sort is stable, so the manager/direct-report groups keep first-appearance order.
	return groups.sort((a, b) => (a.ownerRole === null ? -1 : b.ownerRole === null ? 1 : 0));
};

export const formatAnalysisAsPlainText = (
	data: PostMeetingAnalysisResponseStreamJson,
	meta: { meetingName: string; meetingDate: string; namesByRole: AnalysisNamesByRole },
	settings?: PostMeetingAnalysisSettings | null
): string => {
	// A cached analysis can contain sections an admin has since disabled — omit those (Meeting Overview is always included)
	const isEnabled = (key: keyof PostMeetingAnalysisSettings): boolean => settings?.[key] ?? true;
	const lines: string[] = [];

	lines.push(`Post-Meeting Analysis: ${meta.meetingName}`);
	lines.push(`Meeting Date: ${meta.meetingDate}`);
	lines.push("");

	// Meeting Overview
	if (data.meetingOverview) {
		lines.push(`--- ${sectionTitleMap.meetingOverview} ---`);
		lines.push(data.meetingOverview);
		lines.push("");
	}

	// Decisions (grouped by owner role, matching the panel)
	if (data.decisions && isEnabled("decisionsMade")) {
		lines.push(`--- ${sectionTitleMap.decisions} ---`);
		if (data.decisions.length === 0) {
			lines.push(noDecisionsText);
		} else {
			for (const group of groupDecisionsByOwner(data.decisions)) {
				lines.push(
					group.ownerRole
						? `${toFirstName(meta.namesByRole[group.ownerRole])}'s Decisions`
						: generalDecisionsLabel
				);
				for (const decision of group.decisions) {
					lines.push(`• ${decision}`);
				}
			}
		}
		lines.push("");
	}

	// Missed Commitments
	if (data.missedCommitments && isEnabled("missedCommitments")) {
		lines.push(`--- ${sectionTitleMap.missedCommitments} ---`);
		if (data.missedCommitments.length === 0) {
			lines.push(noMissedCommitmentsText);
		} else {
			for (const commitment of data.missedCommitments) {
				const assignee =
					commitment.assigneeRole === null ? "Unassigned" : meta.namesByRole[commitment.assigneeRole];
				const dueDate = parseCommitmentDueDate(commitment.dueDate);
				const formattedDueDate =
					dueDate && toActionItemDueDate(commitment.dueDate)
						? formatDateTime(dueDate, { hasTime: true, hasDelimiter: true, dateSize: "medium" })
						: null;
				const dueSuffix = formattedDueDate ? ` (Due: ${formattedDueDate})` : "";
				lines.push(`• [${assignee}] ${commitment.description}${dueSuffix}`);
			}
		}
		lines.push("");
	}

	// Participation & Inclusion
	if (
		data.participationAndInclusion &&
		isEnabled("participationAndInclusion") &&
		!isNoDataPlaceholder(data.participationAndInclusion)
	) {
		lines.push(`--- ${sectionTitleMap.participationAndInclusion} ---`);
		lines.push(data.participationAndInclusion);
		lines.push("");
	}

	// Quality of Discussion vs. Monologue
	if (
		data.qualityOfDiscussion &&
		isEnabled("qualityOfDiscussion") &&
		!isNoDataPlaceholder(data.qualityOfDiscussion)
	) {
		lines.push(`--- ${sectionTitleMap.qualityOfDiscussion} ---`);
		lines.push(data.qualityOfDiscussion);
		lines.push("");
	}

	// Time Management
	if (data.timeManagement && isEnabled("timeManagement") && !isNoDataPlaceholder(data.timeManagement)) {
		lines.push(`--- ${sectionTitleMap.timeManagement} ---`);
		lines.push(data.timeManagement);
		lines.push("");
	}

	// Meeting Coaching
	if (data.meetingCoaching && isEnabled("meetingCoaching")) {
		lines.push(`--- ${sectionTitleMap.meetingCoaching} ---`);
		lines.push(`${sectionTitleMap.topStrength}:`);
		lines.push(data.meetingCoaching.topStrength);
		lines.push(`${sectionTitleMap.primaryDevelopmentArea}:`);
		lines.push(data.meetingCoaching.primaryDevelopmentArea);
		lines.push(`${sectionTitleMap.oneThingToTryNextMeeting}:`);
		lines.push(data.meetingCoaching.oneThingToTryNextMeeting);
		lines.push("");
	}

	return lines.join("\n");
};
